import React, { useCallback, useEffect } from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	AIEventsInstrumentationProvider,
	useAIEventsInstrumentation,
} from '@atlassian/ai-analytics';

import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { useSidePanelStateActions } from '@confluence/side-panel-state-container';
import { componentWithFG } from '@confluence/feature-gate-component';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';

import { IssueCreateWarningDialog } from '../work-create-common/IssueCreateWarningDialog.tsx';
import type { IssueCreateSidePanelProps } from '../../__types__/IssueCreateSidePanel.ts';
import { IssueCreateAiLoadingSidebar } from '../ai-work-create/IssueCreateAiLoadingSidebar.tsx';
import {
	useAiIssueCreationEnablementState,
	useIssueCreateSidePanelContext,
} from '../../providers/IssueCreateSidePanelContextProvider.tsx';
import { SingleIssueCreateProvidersWrapper } from '../../providers/SingleIssueCreateProvidersWrapper.tsx';
import { SidePanelWrapper } from '../work-create-common/SidePanelWrapper.tsx';
import { useGetWarningDialogActions } from '../../utils/useGetWarningDialogActions.tsx';

import { IssueCreateSidePanelBody } from './IssueCreateSidePanelBody.tsx';
import { IssueCreateSidePanelFooter } from './IssueCreateSidePanelFooter.tsx';
import { IssueCreateSidePanelHeader } from './IssueCreateSidePanelHeader.tsx';

export const sidePanelTestId = 'issue-create-side-panel';
export const ISSUE_CREATE_SIDE_PANEL_ID = 'issue-create-side-panel';
export const ISSUE_CREATE_SIDE_PANEL_WIDTH = 320;

const sidePanelStyle = xcss({
	position: 'relative',
});

const IssueCreateSidePanelComponent = ({ onClose, flags }: IssueCreateSidePanelProps) => {
	const { showSidePanel, hideSidePanel } = useSidePanelStateActions();
	const { isWarningDialogOpen, handleConfirm, handleCancel } = useGetWarningDialogActions(onClose);

	const [state, actions] = useIssueCreateSidePanelContext();
	const { aiState, isCreatedWithAi, aiAnalyticsContext, isCreatedFromActionItems } = state;
	const aiIssueCreationEnablement = useAiIssueCreationEnablementState();
	const { trackAIInteractionDismiss } = useAIEventsInstrumentation();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const shouldShowAiFooter = isCreatedWithAi && aiState !== 'error';

	const isAiLoading = aiState === 'loading';
	const handleOnClose = useCallback(
		(isCreateSuccess?: boolean) => {
			if (isCreatedWithAi && !isCreateSuccess) {
				trackAIInteractionDismiss({ attributes: aiAnalyticsContext });
			}

			if (!isCreateSuccess) {
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						source: 'confluenceHighlightIssueCreate',
						action: 'closed',
						actionSubject: 'modalDialog',
						actionSubjectId: 'linkCreate',
						type: 'ui',
						attributes: {
							isManuallyCreated: !isCreatedWithAi,
							isCreatedWithAi,
							pageMode: state.pageMode,
							hasEditPermission: state.hasEditPermission,
							isAiLoading,
							aiEnablement: aiIssueCreationEnablement?.enablement,
							isCreatedFromActionItems,
							// We don't invoke table creation from highlighting a single row
							isCreatedFromTable: false,
						},
					},
				}).fire();
			}

			hideSidePanel();
			onClose();
			void actions.setAiState('idle');
		},
		[
			isCreatedWithAi,
			hideSidePanel,
			onClose,
			actions,
			trackAIInteractionDismiss,
			aiAnalyticsContext,
			createAnalyticsEvent,
			state.pageMode,
			isAiLoading,
			aiIssueCreationEnablement?.enablement,
			state.hasEditPermission,
			isCreatedFromActionItems,
		],
	);

	const handleOnConfirm = useCallback(() => {
		void actions.setAiState('idle');
		handleConfirm();
	}, [actions, handleConfirm]);

	useEffect(() => {
		showSidePanel();
	}, [showSidePanel]);

	return (
		<Box testId={sidePanelTestId} xcss={sidePanelStyle}>
			<SidePanelWrapper>
				<IssueCreateAiLoadingSidebar>
					<IssueCreateSidePanelHeader onClose={handleOnClose} />
					<IssueCreateSidePanelBody onClose={handleOnClose} flags={flags} />
					{shouldShowAiFooter && <IssueCreateSidePanelFooter />}
				</IssueCreateAiLoadingSidebar>
			</SidePanelWrapper>
			{isWarningDialogOpen && (
				<IssueCreateWarningDialog
					onCancel={handleCancel}
					onConfirm={handleOnConfirm}
					issuesCount={1}
				/>
			)}
		</Box>
	);
};

const IssueCreateSidePanelComponentWithProvider = ({
	onClose,
	flags,
}: IssueCreateSidePanelProps) => {
	return (
		<SingleIssueCreateProvidersWrapper>
			<AIEventsInstrumentationProvider
				config={{
					// Required configruation
					product: 'confluence',
					subproduct: 'jira',
					aiFeatureName: 'confluenceAiWorkCreation',
					proactiveGeneratedAI: 0,
					userGeneratedAI: 1,
					// Optional configuration
					source: 'confluenceHighlightIssueCreate',
					skipAISessionValidation: true,
				}}
			>
				<IssueCreateSidePanelComponent onClose={onClose} flags={flags} />
			</AIEventsInstrumentationProvider>
		</SingleIssueCreateProvidersWrapper>
	);
};

const IssueCreateSidePanelWithFlags = withFlags(IssueCreateSidePanelComponentWithProvider);

const IssueCreateSidePanelWithErrorBoundary = withErrorBoundary({
	attribution: Attribution.INTEGRATIONS,
})(IssueCreateSidePanelWithFlags);

export const IssueCreateSidePanel: React.FC<Omit<IssueCreateSidePanelProps, 'flags'>> =
	componentWithFG(
		'jira_ai_aiwc_in_confluence_set_up_error_boundary',
		IssueCreateSidePanelWithErrorBoundary,
		IssueCreateSidePanelWithFlags,
	);
